// Constants
import { ClientFlavor, ClientType, persistentStorageSchemaVersion } from "@ms/uno-constants/lib/local/AppConstants";
import { DataPreFetchCompletedVarName, DataPreFetcherEventNames, DataPreFetchSkippedVarName, DataPrefetchViewId, DataPrefetchViewName, } from "@ms/uno-constants/lib/local/DataPreFetcherConstants";
import { legacyTaskListTypePrefix, PlannerSubRouteTypeToViewNameMap, SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { extractBasicPlanRoutingDetailsFromSubRoute, extractPrimaryViewIdFromRoute, } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
import { compareArraysEqual } from "@ms/uno-utilities/lib/local/Comparators";
import { stringToEnum } from "@ms/uno-utilities/lib/local/EnumUtilities";
import { getInitialRoute } from "../../utilities/AppBootstrapRouteUtils";
import { PreFetcherServices } from "./PreFetcherServices";
/**
 * Base Data pre-fetcher that is used to prefetch data before the main application is loaded
 */
export class BaseDataPreFetcher {
    /**
     * Constructor for the base DataPreFetcher
     * @param dataPreFetcherActionCreator The data pre-fetch action creator to use for pre-fetching data
     * @param configProvider The configuration provider for feature flags, settings, and session metadata
     */
    constructor(dataPreFetcherActionCreator, configProvider) {
        this.dataPreFetcherActionCreator = dataPreFetcherActionCreator;
        this.configProvider = configProvider;
        this.preFetcherServices = new PreFetcherServices(this.configProvider, this.getAccessToken.bind(this), this.dataPreFetcherActionCreator);
    }
    /**
     * Start pre-fetching the critical data
     */
    async init() {
        const { EnableDataPreFetcher, EnableDeferDataFetchForBackgroundLoad } = this.configProvider().flights;
        // If data pre-fetch is disabled, return
        if (!EnableDataPreFetcher) {
            return;
        }
        this.mark("PreFetchStarted");
        await this.setupHostDependencies();
        // Should fetch if the pre-fetcher eligibility check is disabled
        const canPreFetch = !EnableDeferDataFetchForBackgroundLoad || (await this.canPreFetch());
        if (canPreFetch) {
            await this.preFetchData();
            this.mark("PreFetchCompleted");
        }
        else {
            this.mark("PreFetchSkipped");
            // Update the window variable and notify that pre-fetch was skipped.
            // Data fetch worker will use this variable to decide whether to wait for pre-fetch completion or not.
            window[DataPreFetchSkippedVarName] = true;
            this.sendPreFetchEvent(DataPreFetcherEventNames.FetchSkipped);
        }
    }
    /**
     * Mark different checkpoints in data pre-fetcher using performance api
     * @param checkpoint Checkpoint to mark
     * @param extraData Extra data to mark
     */
    mark(checkpoint, extraData) {
        const markName = `DataPreFetcher-checkpoint:${checkpoint}`;
        const extraDataEntries = extraData ? Object.entries(extraData).map(([key, value]) => `${key}:${value}`) : [];
        const markNameWithExtraData = [markName, ...extraDataEntries].join("-");
        performance.mark(markNameWithExtraData);
    }
    /**
     * Get initial route of the app
     */
    async getInitialRoute() {
        const { appMetadata, environment } = this.configProvider().sessionMetaData;
        let initialRoute = getInitialRoute(appMetadata.clientFlavor, environment);
        const persistedRoute = await this.getPersistedRoute();
        initialRoute = persistedRoute || initialRoute;
        const initialRouteFromDeepLink = await this.getInitialDeeplinkRoute();
        initialRoute = initialRouteFromDeepLink || initialRoute;
        return initialRoute;
    }
    /**
     * Get persisted route from session storage
     */
    async getPersistedRoute() {
        // If route persistence flight is disabled, return null
        if (!this.configProvider().settings.routePersistenceEnabled) {
            return null;
        }
        let route = null;
        this.currentUserId = await this.getUserId();
        const key = `${this.currentUserId}-uno-${persistentStorageSchemaVersion}-rstore-croute`;
        const serializedRoute = window.localStorage.getItem(key);
        if (!serializedRoute) {
            return null;
        }
        try {
            route = JSON.parse(serializedRoute);
        }
        catch (e) {
            this.mark("PersistedRouteParsingFailed");
        }
        if (!route) {
            return null;
        }
        // Check if what we got is the right shape
        if (!route.subRouteType || stringToEnum(route.subRouteType, SubRouteType) == null) {
            this.mark("InvalidSubRouteTypeInPersistedRoute");
            return null;
        }
        // Check if subRoute format changed, and if so, we need to ensure
        // that we don't use persisted route if it's in legacy TT format
        const subRouteTypesChanged = [SubRouteType.MyDay, SubRouteType.MyTasks, SubRouteType.TodoList];
        if (subRouteTypesChanged.includes(route.subRouteType)) {
            // All TT subroutes start with ["personalApp", "alltasklists", "taskListType"]
            const isTT = route.subRoute.length > 2 && compareArraysEqual(route.subRoute.slice(0, 3), legacyTaskListTypePrefix);
            if (isTT) {
                // Stored route was TT, but we're on native. Return null to ignore this case
                return null;
            }
        }
        return route;
    }
    /**
     * Trigger data pre-fetch based on the current view
     */
    async preFetchData() {
        // Promises to be awaited before sending pre-fetch completion event
        const fetcherPromises = [this.preFetchLeftNavDataIfNeeded()];
        const currentRoute = await this.getInitialRoute();
        const { subRouteType, subRoute } = currentRoute;
        const globalWindowElement = window;
        globalWindowElement[DataPrefetchViewName] = PlannerSubRouteTypeToViewNameMap[subRouteType];
        const partitionKey = extractPrimaryViewIdFromRoute(subRouteType, subRoute, this.currentUserId).partitionKey;
        // Pre-fetch data based on the current view
        switch (subRouteType) {
            case SubRouteType.MyDay:
                fetcherPromises.push(this.preFetchMyDayData());
                globalWindowElement[DataPrefetchViewId] = partitionKey;
                break;
            case SubRouteType.MyTasks:
                fetcherPromises.push(this.preFetchMyTasks());
                globalWindowElement[DataPrefetchViewId] = partitionKey;
                break;
            case SubRouteType.MyPlans:
                fetcherPromises.push(this.preFetchMyPlans());
                globalWindowElement[DataPrefetchViewId] = partitionKey;
                break;
            case SubRouteType.BasicPlan:
                const routingDetails = extractBasicPlanRoutingDetailsFromSubRoute(subRoute);
                if (!routingDetails) {
                    this.mark("InvalidPlanSubRoute");
                }
                else {
                    fetcherPromises.push(this.preFetchPlan(routingDetails.planId));
                    globalWindowElement[DataPrefetchViewId] = partitionKey;
                }
                break;
            default:
                this.mark("PreFetchUnsupported", { subrouteType: subRouteType });
                break;
        }
        // Deep-linked task prefetch is route-agnostic: any route whose subRoute carries a taskId
        this.preFetchDeepLinkedTaskIfNeeded(fetcherPromises, this.getTaskIdFromRoute(subRouteType, subRoute));
        await Promise.all(fetcherPromises);
        // Update the window variable and notify that pre-fetch is completed.
        // Data fetch worker will use this variable to decide whether to wait for pre-fetch completion or not.
        globalWindowElement[DataPreFetchCompletedVarName] = true;
        this.sendPreFetchEvent(DataPreFetcherEventNames.FetchCompleted);
    }
    /**
     * Pre-fetch left nav data if needed
     */
    async preFetchLeftNavDataIfNeeded() {
        if (this.configProvider().sessionMetaData.appMetadata.clientFlavor !== ClientFlavor.App) {
            return;
        }
        await this.preFetcherServices.fetchMruData();
    }
    /**
     * Pre-fetch MyDay data
     */
    async preFetchMyDayData() {
        await this.preFetcherServices.fetchPlannerMyDayTasksData();
    }
    /**
     * Pre-fetch MyTasks data
     */
    async preFetchMyTasks() {
        await Promise.all([this.preFetcherServices.fetchPlannerAssignedToMeTasksData(), this.preFetcherServices.fetchTodoSettings()]);
    }
    /**
     * Pre-fetch MyPlans data
     */
    async preFetchMyPlans() {
        if (this.configProvider().sessionMetaData.appMetadata.clientType !== ClientType.Teams ||
            this.configProvider().settings.tabSettings.disableTabStorage) {
            return;
        }
        await this.preFetcherServices.fetchTTTabsData();
    }
    /**
     * Queue task + chat-message prefetch for a deep-linked task if a taskId is present and the
     * kill-switch changegate has not disabled it. The task fetch runs in parallel with any plan/list
     * fetch; `fetchTask` itself chains to `fetchTaskMessages` when the task reports `hasChat`.
     */
    preFetchDeepLinkedTaskIfNeeded(fetcherPromises, taskId) {
        if (!taskId || this.configProvider().changeGates.DisableTaskAndChatPreFetch) {
            return;
        }
        fetcherPromises.push(this.preFetcherServices.fetchTask(taskId));
    }
    /**
     * Returns the deep-linked taskId from the current subRoute, if any.
     *
     * Restricted to `BasicPlan` deliberately: MyDay and MyTasks can host ToDo tasks (private
     * tasks, flagged emails, etc.) whose IDs aren't valid against the Planner `/tasks(...)`
     * endpoint we use here. Since the route doesn't carry a "task source" signal we can rely on,
     * we only prefetch when the deep link came from a context that is guaranteed Planner.
     */
    getTaskIdFromRoute(subRouteType, subRoute) {
        if (subRouteType === SubRouteType.BasicPlan) {
            return extractBasicPlanRoutingDetailsFromSubRoute(subRoute)?.taskId;
        }
        return undefined;
    }
    /**
     * Pre-fetch plan data
     */
    async preFetchPlan(planId) {
        const fetchPromises = [
            this.preFetcherServices.fetchPlan(planId),
            this.preFetcherServices.fetchBuckets(planId),
            this.preFetcherServices.fetchTasksForPlan(planId),
        ];
        if (this.configProvider().flights.EnableCustomFields) {
            fetchPromises.push(this.preFetcherServices.fetchCustomFieldDefinitions(planId));
        }
        if (this.configProvider().flights.EnableDependencies) {
            fetchPromises.push(this.preFetcherServices.fetchDependenciesForPlan(planId));
        }
        if (this.configProvider().flights.EnableGoalsInPlan && !this.configProvider().changeGates.DisableGoalsPrefetch) {
            fetchPromises.push(this.preFetcherServices.fetchGoalsForPlan(planId));
        }
        await Promise.all(fetchPromises);
    }
    /**
     * Sends a pre-fetcher event
     * @param eventName The event name to send
     */
    sendPreFetchEvent(eventName) {
        const event = new Event(eventName);
        window.dispatchEvent(event);
    }
}
